import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useFileApi } from '../../Hooks/useFileApi';

const containerStyles = {
  p: 2,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100px',
};

export function MediaPreviewPanel({ file }) {
  const [fileUrl, setFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getDownloadUrl } = useFileApi();

  const fileType = (file?.file_type || '').toLowerCase();
  const fileLocation = file?.file_location;

  const isPreviewable = [
    '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp',
    '.mp4', '.webm',
    '.mp3', '.wav'
  ].includes(fileType);

  useEffect(() => {
    const fetchUrl = async () => {
      if (!fileLocation || !isPreviewable) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const url = await getDownloadUrl(fileLocation);
        setFileUrl(url);
      } catch (err) {
        console.error('Error fetching preview URL:', err);
        setError('Failed to load preview');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUrl();
  }, [fileLocation, isPreviewable, getDownloadUrl]);

  if (!isPreviewable) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ ...containerStyles }}>
        <CircularProgress size={24} />
        <Box sx={{ ml: 2 }}>Loading preview...</Box>
      </Box>
    );
  }

  if (error || !fileUrl) {
    return (
      <Box sx={{ ...containerStyles, color: 'text.secondary' }}>
        {error || 'Unable to load preview'}
      </Box>
    );
  }

  if (['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'].includes(fileType)) {
    return (
      <Box sx={containerStyles}>
        <img src={fileUrl} alt={file.filename} style={{ maxWidth: '100%', borderRadius: 8 }} />
      </Box>
    );
  }

  if (['.mp4', '.webm'].includes(fileType)) {
    return (
      <Box sx={containerStyles}>
        <video controls style={{ maxWidth: '100%' }}>
          <source src={fileUrl} type={`video/${fileType.replace('.', '')}`} />
          Your browser does not support the video tag.
        </video>
      </Box>
    );
  }

  if (['.mp3', '.wav'].includes(fileType)) {
    return (
      <Box sx={containerStyles}>
        <audio controls style={{ maxWidth: '100%' }}>
          <source src={fileUrl} type={`audio/${fileType.replace('.', '')}`} />
          Your browser does not support the audio element.
        </audio>
      </Box>
    );
  }

  return null;
}

export default MediaPreviewPanel;
